/**
 * Worship page: hero, intro, when/where, after service, streams (YouTube/VK),
 * Telegram chat, invite CTA, upcoming worship events (from API), link to schedule.
 * Event cards link to existing /event/:id (no separate worship-event entity).
 */

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { ListSkeleton } from "../../components/ui/Skeleton.tsx";
import { EventCard } from "../../components/EventCard.tsx";
import { Loader } from "../../components/Loader.tsx";
import {
  IconDove,
  IconClock,
  IconMapPin,
  IconChevronRight,
  IconPlay,
  IconTelegram,
} from "../../components/Icons.tsx";
import { ErrorDisplay } from "../../components/ErrorDisplay.tsx";
import {
  graphql,
  withMinDelay,
  EVENTS_QUERY,
  CATEGORY_BY_CODE_QUERY,
  type Event,
  type EventWithContent,
} from "../../lib/graphql.ts";
import {
  getDateKeyBangkok,
  getTodayDateKeyBangkok,
  addDaysToDateKey,
  formatTimeBangkok,
  formatDateShortBangkok,
  getDayOfMonthAndWeekdayBangkok,
} from "../../lib/bangkok-time.ts";
import { APP_TIMEZONE } from "../../config.ts";
import { getTelegram } from "../../lib/telegram.ts";
import { sanitizeUrl } from "../../lib/url-utils.ts";

const WEEKDAYS_FULL = [
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
  "Воскресенье",
];

interface WorshipCategory {
  id: string;
  name: string;
  code: string;
  color: string | null;
}

function buildImageUrl(url: string | null | undefined): string | null {
  if (!url?.trim()) return null;
  if (url.startsWith("http")) return url;
  return url.startsWith("/") ? url : `/${url}`;
}

/** Формат "Суббота, 13:00" для блока "Когда и где" */
function formatScheduleFromEvent(startsAt: string): string {
  const dateKey = getDateKeyBangkok(startsAt);
  const { weekdayIndex } = getDayOfMonthAndWeekdayBangkok(dateKey);
  const dayName = WEEKDAYS_FULL[weekdayIndex];
  const timeStr = formatTimeBangkok(startsAt);
  return `${dayName}, ${timeStr}`;
}

/** Кнопка-ссылка наружу (трансляция, карта, чат) */
function ExternalLinkCard({
  href,
  icon: Icon,
  label,
  sublabel,
}: {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  sublabel?: string;
}) {
  // Sanitize URL to prevent XSS via javascript: protocol
  const safeHref = sanitizeUrl(href);
  if (!safeHref) return null;

  return (
    <a
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 py-4 px-4 rounded-xl bg-white/95 shadow-sm border border-slate-100 active:bg-slate-50 transition-colors no-underline text-inherit"
    >
      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-800">{label}</p>
        {sublabel && (
          <p className="text-xs text-slate-500 mt-0.5">{sublabel}</p>
        )}
      </div>
      <IconChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
    </a>
  );
}

export function WorshipPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const [category, setCategory] = useState<WorshipCategory | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // BackButton (Mini App / PWA: назад)
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  useEffect(() => {
    let cancelled = false;
    setCategory(null);
    setError(null);
    graphql<{ categoryByCode: WorshipCategory | null }>(
      CATEGORY_BY_CODE_QUERY,
      { code: "worship" }
    )
      .then((data) => {
        if (cancelled) return;
        setCategory(data.categoryByCode ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Ref to track whether the component is mounted for async safety
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadEvents = useCallback(() => {
    if (!category?.id) return;
    setIsLoadingEvents(true);
    setError(null);
    const fromDate = getTodayDateKeyBangkok();
    const toDate = addDaysToDateKey(fromDate, 120); // ~16 weeks для повторяющихся суббот
    withMinDelay(
      graphql<{ events: Event[] }>(EVENTS_QUERY, {
        categoryIds: [category.id],
        timezone: APP_TIMEZONE,
        fromDate,
        toDate,
      })
    )
      .then((data) => {
        if (!isMountedRef.current) return;
        setEvents(data.events ?? []);
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        setError(err);
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        setIsLoadingEvents(false);
      });
  }, [category?.id]);

  useEffect(() => {
    if (category?.id) loadEvents();
    else setEvents([]);
  }, [category?.id, loadEvents]);

  if (error && !isLoadingEvents) {
    return (
      <Layout>
        <ErrorDisplay
          error={error}
          onRetry={
            category ? loadEvents : () => window.location.reload()
          }
        />
      </Layout>
    );
  }

  /** Одно запланированное богослужение — сразу открываем его страницу */
  if (category && !isLoadingEvents && events.length === 1) {
    return <Navigate to={`/event/${events[0].id}`} replace />;
  }

  const showEventsBlock = category && (isLoadingEvents || events.length > 0);
  const mainEvent = events[0] as EventWithContent | undefined;
  const scheduleStr = mainEvent
    ? formatScheduleFromEvent(mainEvent.startsAt)
    : "";
  const venueStr = mainEvent?.venue ?? "";
  const subtitleStr = mainEvent?.subtitle ?? "";
  const inviteStr = mainEvent?.inviteText ?? "";
  const sloganStr = mainEvent?.slogan ?? "";
  const hasStream =
    Boolean(mainEvent?.streamYoutubeUrl) || Boolean(mainEvent?.streamVkUrl);
  const hasMaps = Boolean(mainEvent?.mapsUrl);
  const hasTelegramChat = Boolean(mainEvent?.telegramChatUrl);
  const hasWhenWhere = mainEvent && (scheduleStr || venueStr);
  const hasHero = Boolean(mainEvent) && Boolean(subtitleStr.trim());
  const heroImageUrl = buildImageUrl(mainEvent?.imageUrl ?? null);
  const descriptionStr = mainEvent?.description?.trim() ?? "";
  const todayDateKey = getTodayDateKeyBangkok();

  const eventsByDay = useMemo(() => {
    const map = new Map<string, Event[]>();
    for (const event of events) {
      const dateKey = getDateKeyBangkok(event.startsAt);
      const existing = map.get(dateKey);
      if (existing) {
        existing.push(event);
      } else {
        map.set(dateKey, [event]);
      }
    }
    for (const dayEvents of map.values()) {
      dayEvents.sort(
        (a, b) =>
          new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
      );
    }
    return map;
  }, [events]);

  const dayKeys = useMemo(
    () => Array.from(eventsByDay.keys()).sort(),
    [eventsByDay]
  );

  return (
    <Layout>
      {/* Hero image — как в HomeGroupsPage */}
      {heroImageUrl ? (
        <div className="aspect-[4/3] overflow-hidden rounded-2xl mb-4 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 bg-slate-100">
          <img
            src={heroImageUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      ) : null}

      {/* Subtitle — без заголовка «Богослужение» */}
      {hasHero ? (
        <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-6 xs:p-8">
          <div className="flex items-center gap-3">
            {!heroImageUrl && (
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0">
                <IconDove className="w-6 h-6" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[15px] text-slate-600">{subtitleStr}</p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Описание — полный текст из события */}
      {descriptionStr ? (
        <div className="mt-6">
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-4 xs:p-5">
            <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap prose prose-slate prose-sm max-w-none">
              {descriptionStr}
            </p>
          </div>
        </div>
      ) : null}

      {/* Программа — из события (program) */}
      {mainEvent?.program && (
        <div className="mt-6">
          <p className="section-title px-0">Программа</p>
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-4 xs:p-5">
            <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">
              {mainEvent.program}
            </p>
          </div>
        </div>
      )}

      {/* Прямая трансляция — из события (streamYoutubeUrl, streamVkUrl) */}
      {hasStream && (
        <div className="mt-6">
          <p className="section-title px-0">Прямая трансляция</p>
          <div className="flex flex-col xs:flex-row gap-3">
            {mainEvent?.streamYoutubeUrl && (
              <ExternalLinkCard
                href={mainEvent.streamYoutubeUrl}
                icon={IconPlay}
                label="Смотреть на YouTube"
                sublabel="Прямой эфир"
              />
            )}
            {mainEvent?.streamVkUrl && (
              <ExternalLinkCard
                href={mainEvent.streamVkUrl}
                icon={IconPlay}
                label="Смотреть ВКонтакте"
                sublabel="Прямой эфир"
              />
            )}
          </div>
        </div>
      )}

      {/* Чат и вопросы — из события (telegramChatUrl) */}
      {hasTelegramChat && mainEvent?.telegramChatUrl && (
        <div className="mt-6">
          <p className="section-title px-0">Чат и вопросы</p>
          <ExternalLinkCard
            href={mainEvent.telegramChatUrl}
            icon={IconTelegram}
            label="Написать в чат"
            sublabel="Вопросы и общение"
          />
        </div>
      )}

      {/* Когда и где — внизу, как на странице мероприятия */}
      {hasWhenWhere ? (
        <div className="mt-6">
          <p className="section-title px-0">Когда и где</p>
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-4 xs:p-5 space-y-3">
            {scheduleStr ? (
              <p className="text-slate-800 font-medium flex items-center gap-2">
                <IconClock className="w-5 h-5 text-slate-500 flex-shrink-0" />
                {scheduleStr}
              </p>
            ) : null}
            {venueStr ? (
              <p className="text-slate-700 flex items-center gap-2">
                <IconMapPin className="w-5 h-5 text-slate-500 flex-shrink-0" />
                {venueStr}
              </p>
            ) : null}
            {hasMaps && mainEvent?.mapsUrl && sanitizeUrl(mainEvent.mapsUrl) ? (
              <a
                href={sanitizeUrl(mainEvent.mapsUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-accent)] no-underline"
              >
                Открыть карту
                <IconChevronRight className="w-4 h-4" />
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Призыв — только из события (inviteText, slogan) */}
      {inviteStr || sloganStr ? (
        <div className="mt-6 rounded-2xl overflow-hidden bg-white/95 shadow-sm p-5 xs:p-6 text-center border border-slate-100">
          {inviteStr ? (
            <p className="font-medium text-slate-800">{inviteStr}</p>
          ) : null}
          {sloganStr ? (
            <p className="text-sm text-slate-600 mt-1">{sloganStr}</p>
          ) : null}
        </div>
      ) : null}

      {/* Ближайшие богослужения — сгруппированы по дням, как в календаре */}
      {showEventsBlock && (isLoadingEvents || events.length > 0) ? (
        <div className="mt-6">
          <p className="section-title px-0">Ближайшие богослужения</p>
          {isLoadingEvents ? (
            <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-6">
              <ListSkeleton count={3} />
            </div>
          ) : (
            <div className="space-y-6">
              {dayKeys.map((dateKey) => {
                const dayEvents = eventsByDay.get(dateKey) ?? [];
                const { weekdayIndex } =
                  getDayOfMonthAndWeekdayBangkok(dateKey);
                const dayName = WEEKDAYS_FULL[weekdayIndex];
                const dateStr = formatDateShortBangkok(dateKey);
                const isToday = dateKey === todayDateKey;

                return (
                  <section key={dateKey}>
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">
                        {isToday ? "Сегодня" : dayName}
                        <span className="font-normal text-slate-400 ml-2">
                          {dateStr}
                        </span>
                      </span>
                      <span className="text-xs text-slate-500">
                        {dayEvents.length}{" "}
                        {dayEvents.length === 1
                          ? "мероприятие"
                          : dayEvents.length < 5
                          ? "мероприятия"
                          : "мероприятий"}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {dayEvents.map((event) => (
                        <EventCard key={event.id} event={event} hideTitle />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {/* Нет богослужений: пульсирующее сердце + текст, затем кнопка расписания */}
      {category && !isLoadingEvents && events.length === 0 ? (
        <div className="mt-6 rounded-2xl overflow-hidden bg-white/80 backdrop-blur-sm shadow-sm p-8 xs:p-10 text-center border border-slate-100">
          <Loader className="mx-auto mb-4" />
          <p className="text-slate-600 text-sm leading-relaxed">
            Пока нет запланированных богослужений.
            <br />
            Следите за расписанием — мы ждём вас.
          </p>
        </div>
      ) : null}

      {/* Всё расписание на неделю */}
      <div className="mt-6">
        <Link
          to="/schedule"
          className="flex items-center justify-between gap-3 py-4 px-4 rounded-xl bg-white/95 shadow-sm border border-slate-100 active:bg-slate-50 transition-colors no-underline text-inherit"
        >
          <span className="font-medium text-slate-800">
            Всё расписание на неделю
          </span>
          <IconChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
        </Link>
      </div>

      <div className="h-4" />
    </Layout>
  );
}
